import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { RotateCcw, ShieldCheck } from "@fleet/icons";
import { Badge } from "@fleet/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@fleet/ui/card";
import { Skeleton } from "@fleet/ui/skeleton";
import { Switch } from "@fleet/ui/switch";
import { Label, TextField } from "@fleet/ui/text-field";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { appConfigQueryKeys, type components } from "@agape/shared";
import { useI18n } from "@/providers/i18n-provider";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type OrgConfig = components["schemas"]["OrgConfigResponse"];
type OrgSettings = components["schemas"]["OrgSettings"];

/** Read path for each logical key inside `OrgSettings`. */
const NUM_READ: Record<string, (s: OrgSettings) => number> = {
  "retention.trash_purge_days": (s) => s.trashPurgeDays,
  "domain.meeting_default_duration_minutes": (s) => s.domain.meetingDefaultDurationMinutes,
  "domain.inventory_low_stock_threshold": (s) => s.domain.inventoryLowStockThreshold,
};

const BOOL_READ: Record<string, (s: OrgSettings) => boolean> = {
  "domain.prayer_private_by_default": (s) => s.domain.prayerPrivateByDefault,
  "domain.prayer_force_private": (s) => s.domain.prayerForcePrivate,
  "domain.reflection_followup_enabled": (s) => s.domain.reflectionFollowupEnabled,
};

const STR_READ: Record<string, (s: OrgSettings) => string> = {
  "domain.meeting_default_timezone": (s) => s.domain.meetingDefaultTimezone,
};

/**
 * Domain parameters for ONE congregation (CONFIG_OVER_CODE, ORG level).
 *
 * What the canon lacks: Agape is multi-church, and default meeting
 * schedules, low-stock threshold, trash retention, and privacy of prayer
 * requests and reflections are PASTORAL decisions of each
 * congregation, not of platform operator.
 *
 * Precedence: **platform > org**. What platform decides is default
 * and CEILING — congregation decides within. When effective value does
 * not match requested value because ceiling trimmed it, panel indicates so: a control
 * that seems to do something but does not is worse than no control.
 *
 * El cliente REFLEJA; quien impone es el write-path (CC-40).
 */
export function CongregationConfig(props: { organizationId?: string }) {
  const { t } = useI18n();

  const query = createApiQuery<OrgConfig, ErrorModel>(
    () => appConfigQueryKeys.org(props.organizationId),
    async () =>
      api.GET("/api/org/config", {
        params: { query: props.organizationId ? { organizationId: props.organizationId } : {} },
      }),
    () => ({ staleTime: 5000 }),
  );

  const upsertMutation = createApiMutation(async (vars: { key: string; value: unknown }) =>
    api.POST("/api/org/config", {
      params: { query: props.organizationId ? { organizationId: props.organizationId } : {} },
      body: vars,
    }),
  );

  const deleteMutation = createApiMutation(async (vars: { key: string }) =>
    api.DELETE("/api/org/config/{key}", {
      params: {
        path: { key: vars.key },
        query: props.organizationId ? { organizationId: props.organizationId } : {},
      },
    }),
  );

  const resolved = () => query.data?.resolved;
  const platform = () => query.data?.platform;
  const overrides = createMemo(() => query.data?.overrides ?? []);
  const overriddenKeys = createMemo(() => new Set(overrides().map((o) => o.key)));
  const catalog = createMemo(() => query.data?.catalog ?? []);

  const refetch = () => {
    void query.refetch();
  };

  const save = (key: string, value: unknown) => {
    upsertMutation.mutate(
      { key, value },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Saved — applied live`), "success");
        },
        onError: () => {
          showToast(t(msg`Could not save this setting`), "error");
        },
      },
    );
  };

  const reset = (key: string) => {
    deleteMutation.mutate(
      { key },
      {
        onSuccess: () => {
          refetch();
          showToast(t(msg`Back to the platform default`), "success");
        },
      },
    );
  };

  /** Did platform ceiling trim what congregation requested? */
  const cappedBy = (key: string): number | undefined => {
    const readNum = NUM_READ[key];
    const eff = resolved();
    const row = overrides().find((o) => o.key === key);
    if (!readNum || !eff || !row || typeof row.value !== "number") {
      return undefined;
    }
    const effective = readNum(eff);
    return effective === row.value ? undefined : effective;
  };

  return (
    <div class="space-y-6" data-testid="congregation-config">
      <div class="flex items-center gap-3">
        <ShieldCheck class="h-7 w-7 text-primary" />
        <div>
          <h2 class="text-2xl font-bold tracking-tight">{t(msg`Congregation settings`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Meeting defaults, low-stock threshold, how long the bin keeps deleted records and the privacy of prayer requests and reflections. The platform sets the default and the ceiling; your congregation decides within it.`,
            )}
          </p>
        </div>
      </div>

      <Show when={query.isError}>
        <QueryErrorState testId="org-config-error" compact onRetry={() => void query.refetch()} />
      </Show>

      <Show when={!query.isError}>
        <Show
          when={!query.isLoading && resolved()}
          fallback={
            <div class="space-y-3">
              <Skeleton class="h-24 w-full rounded-xl" />
              <Skeleton class="h-40 w-full rounded-xl" />
            </div>
          }
        >
          <Card data-testid="org-config-numbers">
            <CardHeader>
              <CardTitle>{t(msg`Domain defaults`)}</CardTitle>
            </CardHeader>
            <CardContent class="grid gap-4 sm:grid-cols-2">
              <For each={catalog().filter((spec) => spec.kind === "int")}>
                {(spec) => {
                  const read = NUM_READ[spec.key];
                  const effective = () => {
                    const s = resolved();
                    return read && s ? read(s) : 0;
                  };
                  const fromPlatform = () => {
                    const s = platform();
                    return read && s ? read(s) : 0;
                  };
                  return (
                    <div class="space-y-1">
                      <div class="flex items-center justify-between gap-2">
                        <Label for={`org-${spec.key}`} class="text-xs font-mono">
                          {spec.key}
                        </Label>
                        <Show
                          when={overriddenKeys().has(spec.key)}
                          fallback={
                            <Badge variant="secondary" data-testid={`org-origin-${spec.key}`}>
                              {t(msg`platform`)}
                            </Badge>
                          }
                        >
                          <Badge data-testid={`org-origin-${spec.key}`}>
                            {t(msg`this congregation`)}
                          </Badge>
                        </Show>
                      </div>
                      <TextField
                        id={`org-${spec.key}`}
                        type="number"
                        value={String(effective())}
                        data-testid={`org-value-${spec.key}`}
                        onChange={(e) => {
                          const parsed = Number.parseInt(e.currentTarget.value.trim(), 10);
                          if (Number.isNaN(parsed) || parsed === effective()) {
                            return;
                          }
                          save(spec.key, parsed);
                        }}
                      />
                      <p class="text-[11px] text-muted-foreground">
                        {spec.unit} · {t(msg`platform`)} = {fromPlatform()}
                      </p>
                      {/* Un mando recortado por el techo TIENE que decirlo. */}
                      <Show when={cappedBy(spec.key) !== undefined}>
                        <p
                          class="text-[11px] text-amber-600 dark:text-amber-500"
                          data-testid={`org-capped-${spec.key}`}
                        >
                          {t(msg`Capped by the platform ceiling`)}
                        </p>
                      </Show>
                      <Show when={overriddenKeys().has(spec.key)}>
                        <button
                          type="button"
                          class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                          data-testid={`org-reset-${spec.key}`}
                          onClick={() => {
                            reset(spec.key);
                          }}
                        >
                          <RotateCcw class="h-3 w-3" />
                          {t(msg`Use the platform default`)}
                        </button>
                      </Show>
                    </div>
                  );
                }}
              </For>

              <For each={catalog().filter((spec) => spec.kind === "str")}>
                {(spec) => {
                  const read = STR_READ[spec.key];
                  const effective = () => {
                    const s = resolved();
                    return read && s ? read(s) : "";
                  };
                  return (
                    <div class="space-y-1">
                      <Label for={`org-${spec.key}`} class="text-xs font-mono">
                        {spec.key}
                      </Label>
                      <TextField
                        id={`org-${spec.key}`}
                        value={effective()}
                        data-testid={`org-value-${spec.key}`}
                        onChange={(e) => {
                          const raw = e.currentTarget.value.trim();
                          if (raw.length === 0 || raw === effective()) {
                            return;
                          }
                          save(spec.key, raw);
                        }}
                      />
                      <p class="text-[11px] text-muted-foreground">{spec.unit}</p>
                    </div>
                  );
                }}
              </For>
            </CardContent>
          </Card>

          <Card data-testid="org-config-privacy">
            <CardHeader>
              <CardTitle>{t(msg`Privacy`)}</CardTitle>
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`The platform can only make these stricter, never looser: if it forces prayer requests private or turns reflection follow-up off, a congregation cannot reopen it.`,
                )}
              </p>
            </CardHeader>
            <CardContent class="space-y-4">
              <For each={catalog().filter((spec) => spec.kind === "bool")}>
                {(spec) => {
                  const read = BOOL_READ[spec.key];
                  const checked = () => {
                    const s = resolved();
                    return read && s ? read(s) : false;
                  };
                  return (
                    <div class="flex items-center justify-between gap-4">
                      <Label class="font-mono text-xs">{spec.key}</Label>
                      <div class="flex items-center gap-3">
                        <Show when={overriddenKeys().has(spec.key)}>
                          <button
                            type="button"
                            class="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                            data-testid={`org-reset-${spec.key}`}
                            onClick={() => {
                              reset(spec.key);
                            }}
                          >
                            <RotateCcw class="h-3 w-3" />
                            {t(msg`Use the platform default`)}
                          </button>
                        </Show>
                        <span data-testid={`org-switch-${spec.key}`}>
                          <Switch
                            checked={checked()}
                            disabled={upsertMutation.isPending}
                            onCheckedChange={(v) => {
                              save(spec.key, v);
                            }}
                          />
                        </span>
                      </div>
                    </div>
                  );
                }}
              </For>
            </CardContent>
          </Card>
        </Show>
      </Show>
    </div>
  );
}
